import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../context/NotificationContext';
import { useTranslation } from 'react-i18next';
import { 
  BellIcon,
  BellAlertIcon,
  ChevronLeftIcon,
  UserCircleIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

interface HeaderProps {
  showBackButton?: boolean;
  title?: string;
  showNotification?: boolean;
  showProfile?: boolean;
  onBackClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ 
  showBackButton = false, 
  title, 
  showNotification = true, 
  showProfile = true,
  onBackClick 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const { i18n, t } = useTranslation();
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [showLanguagePanel, setShowLanguagePanel] = useState(false);
  const [maxWidth, setMaxWidth] = useState('480px');
  const [screenWidth, setScreenWidth] = useState(375);
  
  // Get current language
  const currentLanguage = i18n.language || 'en';
  
  // Language options
  const languageOptions = [
    { code: 'en', label: 'English' },
    { code: 'bn', label: 'বাংলা' },
    { code: 'hi', label: 'हिन्दी' }
  ];
  
  // Handle language change
  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('bondhu_app_language', langCode);
    setShowLanguagePanel(false);
  };

  // Responsive max-width calculation - for header/nav, use 100% to be edge-to-edge
  useEffect(() => {
    const updateResponsive = () => {
      const width = window.innerWidth;
      setScreenWidth(width);
      // Header and nav should be full width edge-to-edge
      setMaxWidth('100%');
    };

    updateResponsive();
    window.addEventListener('resize', updateResponsive);
    return () => window.removeEventListener('resize', updateResponsive);
  }, []);

  // Get page title based on current route
  const getPageTitle = () => {
    if (title) return title;
    return ''; // No page titles
  };

  // Handle back button click
  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate(-1);
    }
  };

  // Handle notification click
  const handleNotificationClick = () => {
    setShowNotificationPanel(!showNotificationPanel);
  };



  // Header container style - full width edge-to-edge, fixed position to prevent scrolling
  const headerStyle = {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px) saturate(180%)',
    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
    paddingTop: 'env(safe-area-inset-top)',
    paddingBottom: '12px',
    width: '100%'
  } as React.CSSProperties;

  // Header content style
  const headerContentStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '56px',
    position: 'relative' as const,
    paddingLeft: '0px',
    paddingRight: '0px',
    overflow: 'visible' as const,
    width: '100%'
  };

  // Left section style
  const leftSectionStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1
  };

  // Back button style
  const backButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    background: 'rgba(0, 0, 0, 0.05)',
    border: 'none',
    color: '#374151',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flexShrink: 0
  };

  // Title style
  const titleStyle = {
    fontSize: '20px',
    fontWeight: '700',
    color: '#111827',
    margin: 0,
    flex: 1,
    textAlign: 'center' as const
  };

  // Right section style
  const rightSectionStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    justifyContent: 'flex-end'
  };

  // Notification button style
  const notificationButtonStyle = {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    background: 'rgba(0, 0, 0, 0.05)',
    border: 'none',
    color: '#374151',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flexShrink: 0
  };

  // Notification badge style
  const notificationBadgeStyle = {
    position: 'absolute' as const,
    top: '6px',
    right: '6px',
    background: '#ef4444',
    color: 'white',
    fontSize: '10px',
    fontWeight: '600',
    borderRadius: '8px',
    padding: '2px 6px',
    minWidth: '16px',
    height: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid white'
  };

  // Language button style
  const languageButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    background: 'rgba(0, 0, 0, 0.05)',
    border: 'none',
    color: '#374151',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flexShrink: 0
  };

  // Profile button style
  const profileButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    background: 'rgba(0, 0, 0, 0.05)',
    border: 'none',
    color: '#374151',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flexShrink: 0
  };
  
  // Language panel style - same positioning approach as notification panel
  // Notification panel uses: position: 'absolute', top: '100%', right: 0 (relative to parent)
  // Language panel should use the same: position: 'absolute', top: '100%', right: 0
  // This will align the panel's right edge to the language button's right edge
  const languagePanelStyle = {
    position: 'absolute' as const,
    top: '100%',
    right: 0,
    width: screenWidth < 375 ? 'calc(100vw - 24px)' : '200px',
    maxWidth: '200px',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
    zIndex: 1002,
    marginTop: '8px'
  } as React.CSSProperties;
  
  // Language option style
  const languageOptionStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: screenWidth < 375 ? '14px 16px' : '12px 16px',
    cursor: 'pointer',
    transition: 'background 0.2s ease',
    background: 'white',
    borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
  };
  
  const selectedLanguageOptionStyle = {
    ...languageOptionStyle,
    background: '#f0f9ff',
    borderLeft: '4px solid #3b82f6'
  };

  // Notification panel style - responsive width
  const notificationPanelStyle = {
    position: 'absolute' as const,
    top: '100%',
    right: 0,
    width: screenWidth < 375 ? 'calc(100vw - 24px)' : '320px',
    maxWidth: '320px',
    maxHeight: '400px',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
    zIndex: 1001,
    marginTop: '8px'
  } as React.CSSProperties;

  // Notification header style
  const notificationHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: '16px',
    paddingBottom: '16px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
    background: '#f9fafb'
  };

  // Notification list style
  const notificationListStyle = {
    maxHeight: '300px',
    overflowY: 'auto' as const
  };

  // Notification item style
  const notificationItemStyle = {
    paddingTop: '12px',
    paddingBottom: '12px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
    cursor: 'pointer',
    transition: 'background 0.2s ease',
    background: 'white'
  };

  const unreadNotificationItemStyle = {
    ...notificationItemStyle,
    background: '#fef3c7'
  };

  return (
    <>
    <div style={headerStyle}>
        <div style={{ ...headerContentStyle, width: '100%', paddingLeft: '16px', paddingRight: '16px' }}>
        {/* Left Section */}
        <div style={leftSectionStyle}>
            {/* Logo */}
            <img 
              src="/SCI Paints Bandhu Logo.png" 
              alt="SCI Paints Bandhu" 
              style={{
                height: '40px',
                width: 'auto',
                cursor: 'pointer',
                transition: 'transform 0.2s ease'
              }}
              onClick={() => navigate('/dashboard')}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            />
            
          {showBackButton && (
            <button
              style={backButtonStyle}
              onClick={handleBackClick}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'scale(0.95)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <ChevronLeftIcon style={{ width: '20px', height: '20px' }} />
            </button>
          )}
        </div>

          {/* Center Title - Hidden */}
          {getPageTitle() && <h1 style={titleStyle}>{getPageTitle()}</h1>}

        {/* Right Section */}
        <div style={rightSectionStyle}>
          {showNotification && (
            <button
              style={notificationButtonStyle}
              onClick={handleNotificationClick}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'scale(0.95)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              {unreadCount > 0 ? (
                <BellAlertIcon style={{ width: '20px', height: '20px', color: '#ef4444' }} />
              ) : (
                <BellIcon style={{ width: '20px', height: '20px' }} />
              )}
              {unreadCount > 0 && (
                <div style={notificationBadgeStyle}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </div>
              )}
            </button>
          )}

          {/* Language Selector Button */}
          <div style={{ position: 'relative' }}>
            <button
              style={languageButtonStyle}
              onClick={() => {
                setShowLanguagePanel(!showLanguagePanel);
                setShowNotificationPanel(false);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'scale(0.95)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <GlobeAltIcon style={{ width: '20px', height: '20px' }} />
            </button>

            {/* Language Selection Panel - Inside relative container like notification panel */}
            {showLanguagePanel && (
              <div 
                style={languagePanelStyle}
                onClick={(e) => e.stopPropagation()}
              >
                {languageOptions.map((lang) => (
                  <div
                    key={lang.code}
                    style={currentLanguage === lang.code ? selectedLanguageOptionStyle : languageOptionStyle}
                    onClick={() => handleLanguageChange(lang.code)}
                    onMouseEnter={(e) => {
                      if (currentLanguage !== lang.code) {
                        e.currentTarget.style.background = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentLanguage !== lang.code) {
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    <span style={{ 
                      fontSize: screenWidth < 375 ? '15px' : '14px', 
                      fontWeight: currentLanguage === lang.code ? '600' : '400',
                      color: currentLanguage === lang.code ? '#3b82f6' : '#111827',
                      flex: 1
                    }}>
                      {lang.label}
                    </span>
                    {currentLanguage === lang.code && (
                      <span style={{ 
                        fontSize: screenWidth < 375 ? '14px' : '12px',
                        color: '#3b82f6',
                        fontWeight: '600',
                        marginLeft: '8px'
                      }}>
                        ✓
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {showProfile && (
            <button
              style={profileButtonStyle}
              onClick={() => navigate('/profile')}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'scale(0.95)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              {user?.photoURL ? (
                <img 
                  src={user.photoURL} 
                  alt="Profile" 
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <UserCircleIcon style={{ width: '24px', height: '24px' }} />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Notification Panel */}
      {showNotificationPanel && (
          <div 
            style={notificationPanelStyle}
            onClick={(e) => e.stopPropagation()}
          >
          <div style={notificationHeaderStyle}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
              Notifications
            </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  transition: 'background 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                Mark all read
              </button>
            )}
                <button
                  onClick={() => setShowNotificationPanel(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6b7280',
                    fontSize: '18px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '6px',
                    transition: 'background 0.2s ease',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(107, 114, 128, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  ×
                </button>
              </div>
          </div>
          
          <div style={notificationListStyle}>
            {notifications.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: '#6b7280' }}>
                <BellIcon style={{ width: '32px', height: '32px', margin: '0 auto 8px', opacity: 0.5 }} />
                <p style={{ margin: 0, fontSize: '14px' }}>No notifications yet</p>
              </div>
            ) : (
                notifications.map((notification) => {
                  // Get notification styling based on type
                  const getTypeColor = (type: string) => {
                    switch (type) {
                      case 'success': return { bg: '#ecfdf5', border: '#10b981', text: '#065f46' };
                      case 'error': return { bg: '#fef2f2', border: '#ef4444', text: '#991b1b' };
                      case 'warning': return { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' };
                      case 'payment': return { bg: '#f0f9ff', border: '#3b82f6', text: '#1e40af' };
                      case 'kyc': return { bg: '#ede9fe', border: '#8b5cf6', text: '#5b21b6' };
                      case 'promo': return { bg: '#fdf4ff', border: '#d946ef', text: '#a21caf' };
                      case 'system': return { bg: '#f3f4f6', border: '#6b7280', text: '#374151' };
                      default: return { bg: '#eff6ff', border: '#3b82f6', text: '#1e40af' };
                    }
                  };

                  const getTypeIcon = (type: string) => {
                    switch (type) {
                      case 'success': return '✅';
                      case 'error': return '❌';
                      case 'warning': return '⚠️';
                      case 'payment': return '💰';
                      case 'kyc': return '📄';
                      case 'promo': return '🎁';
                      case 'system': return '🔔';
                      default: return 'ℹ️';
                    }
                  };

                  const colors = getTypeColor(notification.type);
                  
                  return (
                <div
                  key={notification.id}
                      style={{
                        ...notificationItemStyle,
                        background: notification.read ? 'white' : colors.bg,
                        borderLeft: `4px solid ${colors.border}`,
                        opacity: notification.read ? 0.7 : 1
                      }}
                  onClick={() => markAsRead(notification.id)}
                  onMouseEnter={(e) => {
                        e.currentTarget.style.background = notification.read ? '#f9fafb' : colors.bg;
                        e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                        e.currentTarget.style.background = notification.read ? 'white' : colors.bg;
                        e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{
                          width: '36px',
                          height: '36px',
                      borderRadius: '50%',
                          background: `${colors.border}20`,
                          border: `2px solid ${colors.border}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '16px',
                      flexShrink: 0
                        }}>
                          {getTypeIcon(notification.type)}
                        </div>
                    <div style={{ flex: 1 }}>
                          {notification.title && (
                      <p style={{ 
                        margin: '0 0 4px 0', 
                              fontSize: '13px', 
                              fontWeight: '700',
                              color: colors.text,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              {notification.title}
                            </p>
                          )}
                          <p style={{ 
                            margin: '0 0 6px 0', 
                        fontSize: '14px', 
                        fontWeight: notification.read ? '400' : '600',
                        color: '#111827',
                        lineHeight: '1.4'
                      }}>
                        {notification.message}
                      </p>
                      <p style={{ 
                        margin: 0, 
                            fontSize: '11px', 
                            color: '#9ca3af',
                        fontWeight: '500'
                      }}>
                        {notification.time}
                      </p>
                    </div>
                        {!notification.read && (
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: colors.border,
                            flexShrink: 0,
                            marginTop: '6px'
                          }} />
                        )}
                  </div>
                </div>
                  );
                })
            )}
          </div>
        </div>
      )}
      </div>

      {/* Full Screen Backdrop for notification panel */}
      {showNotificationPanel && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
            background: 'rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(2px)'
          }}
          onClick={() => setShowNotificationPanel(false)}
        />
      )}

      {/* Full Screen Backdrop for language panel */}
      {showLanguagePanel && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
            background: 'rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(2px)'
          }}
          onClick={() => setShowLanguagePanel(false)}
        />
      )}
    </>
  );
};

export default Header;